import {
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  type Auth,
  type User
} from 'firebase/auth';
import { useEffect, useMemo, useState } from 'react';
import { auth, firebaseConfigError, googleProvider } from '../firebase/config';
import { seedDefaultMasters } from '../firebase/repo';

export function useAuth() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(!firebaseConfigError);
  const [error, setError] = useState(firebaseConfigError);

  useEffect(() => {
    if (firebaseConfigError || !auth) {
      setLoading(false);
      setError(firebaseConfigError || 'Firebase Auth is not initialized. Check .env and restart Vite.');
      return;
    }

    return onAuthStateChanged(auth, async (next) => {
      setUser(next);
      setLoading(false);
      setError('');
      if (next) {
        try {
          await seedDefaultMasters(next.uid);
        } catch (seedError) {
          console.error('[PFS seed masters failed]', seedError);
        }
      }
    }, (authError) => {
      console.error('[PFS Firebase auth failed]', authError);
      setUser(null);
      setLoading(false);
      setError(authError.message);
    });
  }, []);

  const requireAuth = (): Auth => {
    if (error) throw new Error(error);
    if (!auth) throw new Error('Firebase Auth is not initialized. Check .env and restart Vite.');
    return auth;
  };

  return useMemo(() => ({
    user,
    loading,
    error,
    signInGoogle: () => signInWithPopup(requireAuth(), googleProvider),
    signInEmail: (email: string, password: string) => signInWithEmailAndPassword(requireAuth(), email, password),
    signUpEmail: (email: string, password: string) => createUserWithEmailAndPassword(requireAuth(), email, password),
    logout: () => signOut(requireAuth())
  }), [user, loading, error]);
}
